import type {
  BranchChangeset,
  BranchChangesets,
  ConfigurationChangeset,
} from '@festina/configuration-general-types/configuration'
import {
  LayerKey,
  OperationLayerMap,
  type WorkspaceKey,
} from '@festina-life/api'
import { assertNotEmpty, notEmpty } from '@festina-life/core'
import { useCallback } from 'react'

import { useWorkspaceOperations } from '../../../../uiModels/workspace/useWorkspaceOperations'
import { downloadConfigurationFile } from '../../../branchesPanel/branchDetails/helpers'

export const useDownloadWorkspacePackage = (workspaceKey: WorkspaceKey) => {
  const { data: operations } = useWorkspaceOperations(workspaceKey)
  const download = useCallback(
    (baseBranchChangesets?: BranchChangesets | null) => {
      const workspaceChangesets = new Map<
        string,
        OperationLayerMap<ConfigurationChangeset>
      >()

      operations.filter(notEmpty).forEach((operation) => {
        const operationLayer = new LayerKey({
          layerId: operation.layer,
          tenantId: operation.tenantId,
        })
        const operationBlock = operation.block
        assertNotEmpty(operationBlock)

        const operationBlockLayers =
          workspaceChangesets.get(operationBlock) ??
          new OperationLayerMap<ConfigurationChangeset>()
        workspaceChangesets.set(operationBlock, operationBlockLayers)

        const operationChangeset =
          operationBlockLayers.get(operationLayer) ??
          ({
            _type: 'ConfigurationChangeset',
            changesetKey: crypto.randomUUID(),
            tenantId: operationLayer.tenantId,
            layer: operationLayer.layerId,
            operations: [],
          } satisfies ConfigurationChangeset)
        operationBlockLayers.set(operationLayer, operationChangeset)
        operationChangeset.operations!.push(operation.operation!)
      })

      const workspaceBranchChangesets = Array.from(
        workspaceChangesets.entries(),
      )
        .flatMap(([block, blockLayers]) =>
          Array.from(blockLayers.values()).map(
            (changeset) =>
              ({
                _type: 'BranchChangeset',
                block,
                changeset,
                changesetHistory: {
                  _type: 'ChangesetHistory',
                  originSets: [
                    {
                      _type: 'OperationOriginSet',
                      operationKeys:
                        changeset.operations
                          ?.map((operation) => operation?.operationKey)
                          .filter(notEmpty) ?? [],
                    },
                  ],
                },
              }) satisfies BranchChangeset,
          ),
        )
        .filter(notEmpty)

      const branchChangesets = {
        _type: 'BranchChangesets',
        branchChangesets: [
          ...(baseBranchChangesets?.branchChangesets ?? []),
          ...workspaceBranchChangesets,
        ],
      } satisfies BranchChangesets

      downloadConfigurationFile(
        branchChangesets,
        `${workspaceKey.branchKey.server}-${workspaceKey.name}-workspace.json`,
      )
    },
    [workspaceKey.branchKey.server, operations, workspaceKey.name],
  )

  return { download, hasOperations: operations.length > 0 }
}
